"use client"

import { useEffect, type ComponentProps, type ReactNode } from "react"
import clsx from "clsx"
import { useFormContext } from "react-hook-form"

import { Accordion, AccordionPanel } from "@unlikelystudio/react-abstract-components"
import { type FiltersPanelProps } from "@unlikelystudio/react-ecommerce-hooks"

import { FACETS, ORDERED_FACETS } from "~/lib/algolia/constants"
import { DISCOUNT_PERCENT_CHAR, DISCOUNT_REPLACER } from "~/lib/algolia/object-serializers/utils/process-discount"
import { SIZES_SEPARATOR } from "~/lib/algolia/object-serializers/utils/process-sizes"
import type { Facet } from "~/lib/algolia/types"
import { CHECKBOX_TYPE_BY_NAME } from "~/lib/algolia/utils/get-checkbox-type-by-name"
import { BoxCheckbox } from "~/components/ui/Panels/Filters/components/Facet/Box"
import { StandardCheckbox } from "~/components/ui/Panels/Filters/components/Facet/Standard"
import { SwatchCheckbox } from "~/components/ui/Panels/Filters/components/Facet/Swatch"
import Icon from "~/components/abstracts/Icon"
import { useFiltersRealTimeSelection } from "~/providers/AlgoliaCustomContext/FiltersRealTimeSelection/index.client"
import { useAlgoliaCustomContext } from "~/providers/AlgoliaCustomContext/index.client"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"
import { objectKeys } from "~/utils/object-keys"
import { objectValues } from "~/utils/object-values"

import * as css from "./styles.css"

type FacetProps = ComponentProps<FiltersPanelProps["facetComponent"]>

export type CheckboxType = "Box" | "List" | "Swatch" | "Radio"

function handleFacetValueDisplay({ name, value }: Pick<FacetProps, "name"> & { value: string }) {
  if (name.startsWith(FACETS.STANDARD_SIZE)) {
    // we needed to process the size string for filters to work
    // please check function processSizes in lib/algolia/object-serializers/utils/process-sizes.ts for further details
    return value.replace(SIZES_SEPARATOR, "")
  }

  if (name.startsWith(FACETS.DISCOUNT)) {
    // we needed to process the discount string for filters to work
    // please check function processDiscount in lib/algolia/object-serializers/utils/process-discount.ts for further details
    return `${value.replaceAll(DISCOUNT_REPLACER, DISCOUNT_PERCENT_CHAR)}`
  }

  return value
}

export function Facet({ name }: FacetProps) {
  const { facets } = useAlgoliaCustomContext()
  const t = useTranslate()

  const { setCurrentSelection } = useFiltersRealTimeSelection()

  const { watch } = useFormContext()
  const watchedValues = watch()

  const unprocessedFacetValues = facets?.[name]
  const hasValidValues = objectValues?.(unprocessedFacetValues ?? {})?.some((value) => value > 0)

  const facetValues =
    objectKeys(unprocessedFacetValues ?? {})
      ?.filter((key) => (unprocessedFacetValues?.[key] || 0) > 0)
      ?.map((key) => ({
        name: key,
        count: unprocessedFacetValues?.[key],
      })) ?? []

  const currentSelection = watchedValues?.[name]
  const selectedValuesAmount = currentSelection ? objectValues(currentSelection)?.filter(Boolean)?.length : 0

  useEffect(() => {
    const filters = currentSelection ? objectValues(currentSelection).filter(Boolean) : []

    setCurrentSelection((previousState) => ({
      ...previousState,
      [name]: filters,
    }))
  }, [selectedValuesAmount])

  if (!hasValidValues) return null

  const facetIndex = ORDERED_FACETS?.findIndex((key) => key === name)

  const type = CHECKBOX_TYPE_BY_NAME?.[name as Facet] ?? "List"

  const dualColumns = Boolean(type === "List" && facetValues?.length > 10)

  if (facetValues?.length <= 1) return null

  function renderCheckbox(entry: (typeof facetValues)[0]) {
    const facetValue = entry?.name
    const facetName = `${name}[${facetValue}]`
    const selected = Boolean(watchedValues?.[name]?.[facetValue])

    if (!facetValue) return null

    const components: Record<CheckboxType, ReactNode> = {
      Box: (
        <BoxCheckbox
          name={facetName}
          value={facetValue}
          checked={selected}
          selected={selected}
          label={handleFacetValueDisplay({ name, value: facetValue })}
        />
      ),
      List: <StandardCheckbox type="checkbox" name={facetName} value={facetValue} checked={selected} />,
      Radio: <StandardCheckbox type="radio" name={facetName} value={facetValue} asDictionaryKey checked={selected} />,
      Swatch: <SwatchCheckbox name={facetName} value={facetValue} selected={selected} />,
    }

    return components?.[type] ?? null
  }

  return (
    <div style={{ order: facetIndex }}>
      <Accordion autoHide={false} index={facetIndex === 0 ? 0 : undefined}>
        <AccordionPanel
          index={0}
          ariaLabels={{ close: t("close"), open: t("open") }}
          head={(isActive) => (
            <div className={css.head}>
              {name && <p>{t(name)}</p>}
              <Icon className={css.icon} name={isActive ? "Minus" : "Plus"} />
            </div>
          )}
          body={
            <div className={clsx(css.body?.[type], { [css.dualColumns]: dualColumns })}>
              {facetValues?.map((entry, index) => (
                <div key={`facetValue-${name}-${index}`} className={css.option}>
                  {renderCheckbox(entry)}
                </div>
              ))}
            </div>
          }
        />
      </Accordion>
    </div>
  )
}
